<template>
  <v-dialog
    max-width="440px"
    v-model="show"
    @click:outside="clickHandler('close')"
    @keydown.esc="clickHandler('close')"
  >
    <v-card>
      <div
        class="d-flex justify-space-between"
      >
        <v-card-title>{{ title || baseContent[mode].title }}</v-card-title>
        <v-btn
          class="mr-1 mt-1"
          icon
          small
          @click="clickHandler('close')"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <v-divider v-if="message || baseContent[mode].message"></v-divider>
      <v-card-text class="py-3">
        {{ message || baseContent[mode].message }}
      </v-card-text>
      <v-divider class="pb-2"></v-divider>
      <v-card-actions v-if="mode === dialogModes.denied">
        <v-spacer></v-spacer>
        <v-btn
          v-bind="buttonsStyle.ok"
          min-width="70px"
          @click="clickHandler('ok')"
        >
          Ok
        </v-btn>
      </v-card-actions>
      <v-card-actions
        v-else-if="mode === dialogModes.delete"
      >
        <v-btn
          v-bind="buttonsStyle.cancel"
          @click="clickHandler('cancel')"
        >
          Cancel
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          v-bind="buttonsStyle.delete"
          @click="clickHandler('delete')"
        >
          Delete
        </v-btn>
      </v-card-actions>
      <v-card-actions
        v-else-if="mode === dialogModes.confirmation"
      >
        <v-btn
          v-bind="buttonsStyle.cancel"
          @click="clickHandler('cancel')"
        >
          Cancel
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          v-bind="buttonsStyle.ok"
          min-width="70px"
          @click="clickHandler(confirmBtnName)"
        >
          {{ confirmBtnName }}
        </v-btn>
      </v-card-actions>
      <v-card-actions
        v-else-if="mode === dialogModes.custom && buttons.length"
      >
        <div
          :class="buttons.length === 2 ? 'buttonsContainerTwoBtn' : 'buttonsContainerAllExceptTwoBtn'"
        >
          <v-btn
            v-for="button in buttons"
            :key="button.name"
            class="ma-1"
            min-width="70px"
            v-bind="button.attrs"
            @click="clickHandler(button.name)"
          >
            {{ button.name }}
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
const DEFAULT_BUTTONS_STYLE = {
  confirm: {
    small: true,
    color: 'primary'
  },
  cancel: {
    small: true,
    outlined: true,
    color: 'primary'
  },
  ok: {
    small: true,
    color: 'primary'
  },
  delete: {
    small: true,
    color: 'red darken-2 white--text'
  }
}

export default {
  name: 'ConfirmOrDeniedDialog',
  props: {
    value: {
      required: false,
      type: Boolean,
      default: false
    },
    mode: {
      required: false,
      validator: function (value) {
        return ['denied', 'delete', 'confirmation', 'custom'].includes(value)
      },
      default: 'denied'
    },
    title: {
      required: false,
      type: String
    },
    message: {
      required: false,
      type: String
    },
    confirmBtnName: {
      required: false,
      type: String,
      default: 'confirm'
    },
    btnAttrs: {
      type: Object,
      required: false,
      default: () => ({}),
      validator: (value) => {
        const baseButtonsName = ['cancel', 'confirm', 'ok', 'delete']
        for (const key in value) {
          if (typeof value[key] !== 'object' || value[key] === null) {
            return false
          }
        }
        const keys = Object.keys(value)
        for (const key of keys) {
          const lowercaseKey = key.toLowerCase()
          if (!baseButtonsName.includes(lowercaseKey)) {
            return false
          }
        }
        return true
      }
    },
    buttons: {
      type: Array,
      required: false,
      default: () => [],
      validator: (value) => {
        return value.every((item) => {
          return (
            typeof item === 'object' && item !== null &&
            item.name && typeof item.name === 'string' &&
            item.attrs && typeof item.attrs === 'object' &&
            Object.values(item.attrs).length
          )
        })
      }
    }
  },
  data () {
    return {
      baseContent: {
        delete: {
          title: 'Delete',
          message: 'Are you sure?'
        },
        denied: {
          title: 'Denied',
          message: 'The operation was denied!'
        },
        confirmation: {
          title: 'Confirmation',
          message: 'Are you sure you want to save the changes?'
        },
        custom: {
          title: '',
          message: ''
        }
      }
    }
  },
  computed: {
    show: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },
    dialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    buttonsStyle () {
      const styles = {
        confirm: { ...DEFAULT_BUTTONS_STYLE.confirm },
        cancel: { ...DEFAULT_BUTTONS_STYLE.cancel },
        ok: { ...DEFAULT_BUTTONS_STYLE.ok },
        delete: { ...DEFAULT_BUTTONS_STYLE.delete }
      }

      if (Object.keys(this.btnAttrs).length) {
        if (this.mode === this.dialogModes.denied) {
          Object.assign(styles.ok, this.btnAttrs.ok)
        } else if (this.mode === this.dialogModes.delete) {
          Object.assign(styles.delete, this.btnAttrs.delete)
          Object.assign(styles.cancel, this.btnAttrs.cancel)
        } else if (this.mode === this.dialogModes.confirmation) {
          Object.assign(styles.ok, styles.confirm, this.btnAttrs.confirm)
          Object.assign(styles.cancel, this.btnAttrs.cancel)
        }
      }

      return styles
    }
  },
  methods: {
    clickHandler (name) {
      const customEventName = name.trim().split(' ').map(el => el.charAt(0).toUpperCase() + el.substring(1)).join('')
      this.$emit('on' + customEventName + 'Click')
      this.show = false
    }
  }
}
</script>
<style lang="scss" scoped>
%buttonsContainer {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
}

.buttonsContainerTwoBtn {
  @extend %buttonsContainer;
  justify-content: space-between;
}

.buttonsContainerAllExceptTwoBtn {
  @extend %buttonsContainer;
  justify-content: flex-end;
}
</style>
